import { resolveKyrosApiUrl } from "./apiConfig";

const AUTH_STORAGE_KEY = "kyros_auth_v1";
const LOCAL_RATINGS_KEY = "kyros_product_ratings_v1";

export function readStoredAuth() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || "null");
  } catch (_error) {
    return null;
  }
}

function readLocalRatings() {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_RATINGS_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function writeLocalRatings(ratings) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_RATINGS_KEY, JSON.stringify(ratings));
}

export function getLocalProductRating(productCode) {
  const code = String(productCode || "").trim().toLowerCase();
  if (!code) return 0;
  const value = Number(readLocalRatings()[code]);
  return Number.isInteger(value) && value >= 1 && value <= 5 ? value : 0;
}

export function setLocalProductRating(productCode, rating) {
  const code = String(productCode || "").trim().toLowerCase();
  const value = Number(rating);
  if (!code || !Number.isInteger(value) || value < 1 || value > 5) {
    return;
  }
  const ratings = readLocalRatings();
  ratings[code] = value;
  writeLocalRatings(ratings);
}

export function normalizeProductRating(productOrRating, productCode) {
  const source =
    productOrRating && typeof productOrRating === "object"
      ? productOrRating.rating || productOrRating.ratings || productOrRating
      : {};
  const metadata =
    productOrRating && typeof productOrRating.metadata === "object" ? productOrRating.metadata : {};
  const metadataRating =
    metadata && typeof metadata.rating === "object" ? metadata.rating : {};

  const averageCandidate =
    source.average ??
    source.ratingAverage ??
    source.rating_average ??
    source.average_rating ??
    productOrRating?.ratingAverage ??
    productOrRating?.rating_average ??
    metadataRating.average ??
    metadataRating.ratingAverage;
  const countCandidate =
    source.count ??
    source.ratingCount ??
    source.rating_count ??
    source.total ??
    productOrRating?.ratingCount ??
    productOrRating?.rating_count ??
    metadataRating.count ??
    metadataRating.ratingCount;
  const userCandidate =
    source.userRating ??
    source.user_rating ??
    productOrRating?.userRating ??
    productOrRating?.user_rating ??
    metadataRating.userRating;

  const average = Number(averageCandidate);
  const count = Number(countCandidate);
  const userRating = Number(userCandidate) || getLocalProductRating(productCode);

  return {
    average: Number.isFinite(average) && average > 0 ? Math.min(5, Math.max(0, average)) : 0,
    count: Number.isFinite(count) && count > 0 ? Math.max(0, Math.round(count)) : 0,
    userRating: Number.isInteger(userRating) && userRating >= 1 && userRating <= 5 ? userRating : 0,
  };
}

export async function fetchProductRating(productCode, token) {
  const code = encodeURIComponent(String(productCode || "").trim());
  if (!code) throw new Error("product_code_required");
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(resolveKyrosApiUrl(`/auth/store/products/${code}/rating`), {
    headers,
  });
  if (!response.ok) {
    throw new Error(`rating_get_${response.status}`);
  }
  return response.json();
}

export async function submitProductRating(productCode, rating, token) {
  const code = encodeURIComponent(String(productCode || "").trim());
  const value = Number(rating);
  if (!code || !Number.isInteger(value) || value < 1 || value > 5) {
    throw new Error("rating_invalid");
  }
  if (!token) {
    throw new Error("rating_auth_required");
  }
  const response = await fetch(resolveKyrosApiUrl(`/auth/store/products/${code}/rating`), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ rating: value }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data.message || data.error || `rating_put_${response.status}`;
    throw new Error(message);
  }
  return data;
}

